import { round } from './Calculator'

// Foreign-currency display layer for the payment form.
//
// All payment state and allocation math live in the accounting book's
// SETTLEMENT currency (e.g. TWD/JPY). When the user picks a non-settlement
// currency (e.g. USD) we only convert at the display/input edges:
//   - toDisplay:    settlement amount -> what the user sees/edits (USD)
//   - toSettlement: what the user typed (USD) -> settlement amount stored
//
// `rate` is the settlement-units-per-1-foreign-unit rate (e.g. TWD per 1 USD),
// i.e. the value persisted as payments.exchange_rate. So:
//   settlement = foreign * rate     and     foreign = settlement / rate
//
// When `isForeign` is false (or no rate yet) both functions are the identity,
// so settlement-currency books behave exactly as before.

// settlement(book) -> selected display currency.
export const toDisplay = (settlement, { isForeign, rate, displayExponent } = {}) => {
  const n = parseFloat(settlement)
  if (!isFinite(n)) { return settlement }
  return (isForeign && rate) ? round(n / rate, displayExponent) : n
}

// selected display currency -> settlement(book) currency.
export const toSettlement = (foreign, { isForeign, rate, bookExponent } = {}) => {
  const n = parseFloat(foreign)
  if (!isFinite(n)) { return foreign }
  return (isForeign && rate) ? round(n * rate, bookExponent) : n
}

// Distribute `total` across buckets weighted by `weights`, rounded to `exponent`,
// so the result sums to EXACTLY `total` (largest-remainder method). Used at
// submit time to convert per-row foreign allocations into settlement-currency
// allocations that (a) sum exactly to the settlement total and (b) make the
// payer side and ower side agree — the API rejects payments where they don't.
//
// Zero/empty weights -> an even split. A zero total -> all zeros.
export const apportion = (weights, total, exponent = 0) => {
  const n = weights.length
  if (n === 0) { return [] }

  const factor = Math.pow(10, exponent)
  const totalUnits = Math.round(parseFloat(total) * factor)
  if (!isFinite(totalUnits) || totalUnits === 0) { return weights.map(() => 0) }

  const w = weights.map((x) => {
    const v = parseFloat(x)
    return isFinite(v) && v > 0 ? v : 0
  })
  let weightSum = w.reduce((a, b) => a + b, 0)
  // No usable weights -> even split.
  const effective = weightSum > 0 ? w : w.map(() => 1)
  weightSum = effective.reduce((a, b) => a + b, 0)

  const exact = effective.map((x) => (x / weightSum) * totalUnits)
  const floors = exact.map((x) => Math.floor(x))
  let remainder = totalUnits - floors.reduce((a, b) => a + b, 0)

  // Hand out the leftover units to the largest fractional parts first.
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac)

  const units = floors.slice()
  for (let k = 0; k < remainder; k++) {
    units[order[k % n].i] += 1
  }

  return units.map((u) => u / factor)
}

const currencyDisplay = { toDisplay, toSettlement, apportion }
export default currencyDisplay
